import React from 'react';
import { DateTime } from 'src_common/utils/time/time';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import { ResponsibleGamblingTabItemsRoute } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs';
import {
    ChevronDownIconWrapper,
    Select,
    SelectWrapper,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import { SelfExclusionNotificationType } from 'src_server/types/notificationData';
import {
    isSelfExclusionAppliedNotification,
    isSelfExclusionRemovedNotification,
} from './selfExclusionNotificationUtils';
import {
    ColumnHeader,
    SectionHeader,
    SectionMessage,
    SectionWrapper,
    TableCell,
    TableHeaderRow,
    TableRow,
    TableWrapper,
} from './SelfExcludedTab.style';
import { SelfExcludedTabState } from './SelfExcludedTab.state';
import { Button } from 'src/domains/common/components/button/button';

const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
} as const;

type ActionsType = keyof typeof ACTIONS;

const SELF_EXCLUSION_AUDIT_ROUTE_PARAMS = {
    tab: 'customer',
    'tab.tab': 'responsible-gambling',
    'tab.tab.tab': ResponsibleGamblingTabItemsRoute.SELFEXCLUSION,
    audits: 'selfExclusionAudit',
} as const;

interface OptionType {
    action: string;
    name: string;
}

const getOption = (option: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${option.action}`}
            value={option.action}
        >
            {option.name}
        </option>
    );
};

interface SelfExcludedSectionPropsType {
    type: ActionsType;
    header: string;
    dataTest: string;
    notifications: Array<SelfExclusionNotificationType>;
    state: SelfExcludedTabState;
    isFinished: boolean;
}

const formatDateTime = (value: string | undefined): string =>
    value === undefined ? '-' : (DateTime.from(value)?.format('DD-MM-YYYY HH:mm') ?? '-');

export const SelfExcludedSection = observer(
    'SelfExcludedSection',
    ({
        type,
        header,
        dataTest,
        notifications,
        state,
        isFinished,
    }: SelfExcludedSectionPropsType): React.ReactElement => {
        const { models, reduxAction, routerBackoffice } = useAppStateContext();

        const openSelfExclusionAudit = (customerId: number): void => {
            const currentCustomerId = routerBackoffice.getValueString('customers');
            reduxAction.customersActionsOpenRequest(customerId, undefined, SELF_EXCLUSION_AUDIT_ROUTE_PARAMS);
            if (currentCustomerId === String(customerId)) {
                routerBackoffice.redirectToNamespace(`customers:${customerId}`, SELF_EXCLUSION_AUDIT_ROUTE_PARAMS);
            }
        };

        const doAction = async (
            event: React.ChangeEvent<HTMLSelectElement>,
            notification: SelfExclusionNotificationType
        ): Promise<void> => {
            const action = event.target.value;

            const mutationAction =
                action === 'unassign'
                    ? 'unassign'
                    : action === 'assign'
                      ? 'assign'
                      : action === 'cancel'
                        ? 'close'
                        : null;

            if (mutationAction === null) {
                return;
            }

            try {
                await state.updateNotification(notification, mutationAction);
            } catch (error) {
                console.error(`SelfExcluded ${action} error`, error);
            }
        };

        const renderAuditCell = (customerId: number, dataTestSuffix: string): React.ReactElement => (
            <TableCell data-test={dataTestSuffix}>
                <Button
                    onClick={(): void => openSelfExclusionAudit(customerId)}
                    variant='link'
                    icon='external-link'
                    size='sm'
                    dataTest={`${dataTestSuffix}-button`}
                >
                    Self Exclusion Audit
                </Button>
            </TableCell>
        );

        const renderActionCell = (
            notification: SelfExclusionNotificationType,
            dataTestSuffix: string
        ): React.ReactElement => (
            <TableCell disableColor={true}>
                <SelectWrapper>
                    <Select
                        name='action'
                        defaultValue=''
                        onChange={(event): void => {
                            void doAction(event, notification);
                        }}
                        data-test={dataTestSuffix}
                    >
                        <option value=''>Choose an action</option>
                        {ACTIONS[type].map((option) => getOption(option, notification.id))}
                    </Select>
                    <ChevronDownIconWrapper />
                </SelectWrapper>
            </TableCell>
        );

        const areNoItems = isFinished && notifications.length === 0;
        const appliedNotifications = notifications.filter(isSelfExclusionAppliedNotification);
        const removedNotifications = notifications.filter(isSelfExclusionRemovedNotification);

        return (
            <SectionWrapper data-test={dataTest}>
                <SectionHeader data-test='section-header'>{header}</SectionHeader>
                {areNoItems ? (
                    <SectionMessage>There are no items</SectionMessage>
                ) : (
                    <>
                        {appliedNotifications.length > 0 ? (
                            <TableWrapper>
                                <thead>
                                    <TableHeaderRow>
                                        <ColumnHeader>Date Detected</ColumnHeader>
                                        <ColumnHeader>Customer</ColumnHeader>
                                        <ColumnHeader>Self Exclusion Audit</ColumnHeader>
                                        <ColumnHeader>Self Exclusion Starts</ColumnHeader>
                                        <ColumnHeader>Reactivation Allowed After</ColumnHeader>
                                        <ColumnHeader>Action</ColumnHeader>
                                    </TableHeaderRow>
                                </thead>
                                <tbody>
                                    {appliedNotifications.map((notification) => (
                                        <TableRow
                                            key={notification.id}
                                            section={type}
                                            data-test={`self-exclusion-applied-notification-row-${notification.accountId}`}
                                        >
                                            <TableCell data-test='self-exclusion-applied-date-detected'>
                                                {formatDateTime(notification.createdAt)}
                                            </TableCell>
                                            <TableCell data-test='self-exclusion-applied-customer'>
                                                <CustomerButton
                                                    customerId={notification.accountId}
                                                    models={models}
                                                />
                                            </TableCell>
                                            {renderAuditCell(notification.accountId, 'self-exclusion-applied-audit')}
                                            <TableCell data-test='self-exclusion-applied-starts-at'>
                                                {formatDateTime(notification.details.startsAt)}
                                            </TableCell>
                                            <TableCell data-test='self-exclusion-applied-reactivation'>
                                                {formatDateTime(notification.details.reactivationAllowedAfter)}
                                            </TableCell>
                                            {renderActionCell(notification, 'self-exclusion-applied-action-form')}
                                        </TableRow>
                                    ))}
                                </tbody>
                            </TableWrapper>
                        ) : null}
                        {removedNotifications.length > 0 ? (
                            <TableWrapper>
                                <thead>
                                    <TableHeaderRow>
                                        <ColumnHeader>Date Detected</ColumnHeader>
                                        <ColumnHeader>Customer</ColumnHeader>
                                        <ColumnHeader>Self Exclusion Audit</ColumnHeader>
                                        <ColumnHeader>Self Exclusion Started</ColumnHeader>
                                        <ColumnHeader>Self Exclusion Removed</ColumnHeader>
                                        <ColumnHeader>Action</ColumnHeader>
                                    </TableHeaderRow>
                                </thead>
                                <tbody>
                                    {removedNotifications.map((notification) => (
                                        <TableRow
                                            key={notification.id}
                                            section={type}
                                            data-test={`self-exclusion-removed-notification-row-${notification.accountId}`}
                                        >
                                            <TableCell data-test='self-exclusion-removed-date-detected'>
                                                {formatDateTime(notification.createdAt)}
                                            </TableCell>
                                            <TableCell data-test='self-exclusion-removed-customer'>
                                                <CustomerButton
                                                    customerId={notification.accountId}
                                                    models={models}
                                                />
                                            </TableCell>
                                            {renderAuditCell(notification.accountId, 'self-exclusion-removed-audit')}
                                            <TableCell data-test='self-exclusion-removed-starts-at'>
                                                {formatDateTime(notification.details.startsAt)}
                                            </TableCell>
                                            <TableCell data-test='self-exclusion-removed-removed-at'>
                                                {formatDateTime(notification.details.removedAt)}
                                            </TableCell>
                                            {renderActionCell(notification, 'self-exclusion-removed-action-form')}
                                        </TableRow>
                                    ))}
                                </tbody>
                            </TableWrapper>
                        ) : null}
                    </>
                )}
            </SectionWrapper>
        );
    }
);
